// ═══════════════════════════════════════════════════════════════════
//  modules/hub/HubSuivi.js
//  L'état du Hub : joint ou non, qui y est, ce qu'il déclare.
// ═══════════════════════════════════════════════════════════════════

import { THEME } from "../../socle/theme";
import { Panel } from "../../composants/Panel";

const enc = { padding: "12px 14px", borderRadius: 10, fontSize: 13, lineHeight: 1.55, marginBottom: 14 };

export function HubSuivi({ etat, onRafraichir, chargement }) {
  if (!etat) return null;

  const bulle = (ok, texte) => (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 7,
      color: ok ? THEME.emerald : THEME.red, fontWeight: 700, fontSize: 14,
    }}>
      <span style={{
        width: 9, height: 9, borderRadius: "50%",
        background: ok ? THEME.emerald : THEME.red, display: "inline-block",
      }} />
      {texte}
    </span>
  );

  return (
    <div>
      {/* ── Le cas qui compte : le RCON n'est pas configuré ──
          On ne montre pas un serveur « hors ligne » alors qu'on ne lui a
          jamais parlé. Dire « je n'ai pas l'adresse » et « il ne répond
          pas » sont deux informations différentes, et les confondre
          enverrait chercher une panne qui n'existe pas. */}
      {!etat.configure && (
        <div style={{ ...enc, background: THEME.amberDim, border: `1px solid ${THEME.amber}`, color: THEME.text }}>
          <b>Le Hub n'est pas encore joignable depuis l'intranet.</b>
          <div style={{ marginTop: 8, color: THEME.textMuted }}>
            Trois lignes à ajouter dans <code>C:\inetpub\orialis-admin\.env</code>, à récupérer
            dans le panneau Minestrator :
          </div>
          <pre style={{
            margin: "10px 0 0", padding: "10px 12px", background: THEME.bg,
            border: `1px solid ${THEME.border}`, borderRadius: 8,
            color: THEME.text, fontSize: 12.5, overflowX: "auto",
          }}>{`RCON_HUB_HOST=...\nRCON_HUB_PORT=...\nRCON_HUB_PASSWORD=...`}</pre>
          <div style={{ marginTop: 10, color: THEME.textMuted }}>
            Puis <code>net stop OrialisIntranet</code> et <code>net start OrialisIntranet</code>.
            L'onglet Permissions, lui, fonctionne déjà : il lit la base partagée, qui n'a jamais
            eu besoin du Hub.
          </div>
        </div>
      )}

      <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>

        <Panel title="État"
          action={
            <button onClick={onRafraichir} disabled={chargement}
              style={{
                background: THEME.surface, color: THEME.text, border: `1px solid ${THEME.border}`,
                borderRadius: 8, padding: "5px 12px", fontSize: 12, cursor: chargement ? "default" : "pointer",
                opacity: chargement ? 0.5 : 1,
              }}>
              {chargement ? "…" : "Rafraîchir"}
            </button>
          }>
          {!etat.configure
            ? <span style={{ color: THEME.textMuted, fontSize: 14 }}>inconnu — pas d'adresse RCON</span>
            : bulle(etat.en_ligne, etat.en_ligne ? "En ligne" : "Injoignable")}
          {etat.message && (
            <div style={{ marginTop: 10, color: THEME.textMuted, fontSize: 12.5, lineHeight: 1.5 }}>
              {etat.message}
            </div>
          )}
        </Panel>

        <Panel title="Joueurs">
          <div style={{ fontSize: 28, fontWeight: 700, color: THEME.text, lineHeight: 1 }}>
            {etat.en_ligne ? etat.joueurs.length : "—"}
            {etat.max_joueurs ? (
              <span style={{ fontSize: 15, color: THEME.textMuted, fontWeight: 500 }}> / {etat.max_joueurs}</span>
            ) : null}
          </div>
          <div style={{ marginTop: 10, color: THEME.textMuted, fontSize: 13, lineHeight: 1.5 }}>
            {etat.en_ligne
              ? (etat.joueurs.length ? etat.joueurs.join(" · ") : "personne")
              : "serveur non interrogé"}
          </div>
        </Panel>

        <Panel title="Permissions déclarées">
          <div style={{ fontSize: 28, fontWeight: 700, color: THEME.text, lineHeight: 1 }}>{etat.nodes}</div>
          <div style={{ marginTop: 10, color: THEME.textMuted, fontSize: 12.5, lineHeight: 1.5 }}>
            {etat.nodes === 0
              ? <>Aucune. <b style={{ color: THEME.amber }}>OrialisPermDump n'a pas encore parlé</b> depuis
                  ce serveur, ou n'y est pas installé avec <code>serveur: hub</code>.</>
              : <>vues sur le Hub{etat.vu_le ? <> · dernier relevé {String(etat.vu_le).slice(0, 16).replace("T", " ")}</> : null}</>}
          </div>
        </Panel>
      </div>

      {etat.plugins && etat.plugins.length > 0 && (
        <Panel title="Plugins vus sur le Hub" style={{ marginTop: 14 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {etat.plugins.map((p) => (
              <span key={p.nom} style={{
                background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 999,
                padding: "5px 12px", fontSize: 12.5, color: THEME.text,
              }}>
                {p.nom} <span style={{ color: THEME.textMuted }}>· {p.n}</span>
              </span>
            ))}
          </div>
          <div style={{ marginTop: 12, color: THEME.textDim, fontSize: 12, lineHeight: 1.5 }}>
            Déduits du catalogue, pas d'un appel au serveur : c'est ce que le plugin de découverte
            a rapporté au dernier démarrage du Hub.
          </div>
        </Panel>
      )}

      {etat.journal && etat.journal.length > 0 && (
        <Panel title="Dernières permissions appliquées" style={{ marginTop: 14 }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <thead>
                <tr style={{ color: THEME.textMuted, textAlign: "left" }}>
                  <th style={{ padding: "6px 10px", fontWeight: 600 }}>Quand</th>
                  <th style={{ padding: "6px 10px", fontWeight: 600 }}>Qui</th>
                  <th style={{ padding: "6px 10px", fontWeight: 600 }}>Permission</th>
                  <th style={{ padding: "6px 10px", fontWeight: 600 }}>Grade</th>
                  <th style={{ padding: "6px 10px", fontWeight: 600 }}>Devient</th>
                </tr>
              </thead>
              <tbody>
                {etat.journal.map((l, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${THEME.border}`, color: THEME.text }}>
                    <td style={{ padding: "6px 10px", color: THEME.textMuted, whiteSpace: "nowrap" }}>
                      {String(l.applique_le).replace("T", " ").slice(0, 16)}
                    </td>
                    <td style={{ padding: "6px 10px", color: THEME.textMuted }}>{l.auteur}</td>
                    <td style={{ padding: "6px 10px" }}>{l.node}</td>
                    <td style={{ padding: "6px 10px" }}>{l.grade}</td>
                    <td style={{ padding: "6px 10px", color: l.apres === "true" ? THEME.emerald : l.apres === "false" ? THEME.red : THEME.textMuted }}>
                      {l.apres}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 12, color: THEME.textDim, fontSize: 12, lineHeight: 1.5 }}>
            Ce journal n'est pas propre au Hub : les trois serveurs partagent une seule base de
            permissions. Il est ici parce que c'est là qu'on regarde ce qui vient d'être appliqué.
            <br />
            <b>Un « ok » ne prouve rien</b> : les commandes <code>lp</code> ne répondent pas par
            RCON, le statut est donc optimiste par construction. La preuve est la relecture de la
            base — <code>POSER_PERMISSIONS.bat</code>, choix 3.
          </div>
        </Panel>
      )}
    </div>
  );
}
